import React, { MouseEventHandler, useMemo } from 'react';
import { useHideOverflowing } from '../common/useHideOverflowing';
import { useTranslation } from 'react-i18next';
import {
  Route,
  getRouteHealthStatuses,
  Organization,
  ProviderType,
  DestinationType,
  Destination,
} from '../common/ObjectTypes';
import Pagination from '../common/Pagination';
import GlobalSearch from '../common/GlobalSearch';
import {
  listPageClasses,
  buttonClasses,
  filterClasses,
  paginationClasses,
  skeletonClasses,
} from '../common/tailwindClassnames';
import {
  CopyToClipboard,
  ArrowForward,
  RefreshSpinner,
} from '../common/ProjectIcons';
import { toast } from 'react-toastify';
import NoResults from '../common/NoResults';
import { page_size } from '../../api/apiUtils';
import { getRouteStatus } from './getRouteStatus';
import { routeDisplayName } from './routeDisplayName';
import { routeRecordId } from './routeDisplayName';
import { HoverTooltip } from '../common/HoverTooltip';
import { isEarthRangerTypeValue } from '../destinations/earthRangerUrl';
import SelectInput from '../common/SelectInput';
import SortByDropdown, { SortOption } from '../common/SortByDropdown';

const getHostname = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

type DestWithDisplay = Destination & { displayUrl: string; hasUrl: boolean };

const DestinationCellContent = React.memo(
  ({
    destinations,
    t,
  }: {
    destinations: Destination[];
    t: (key: string, opts?: any) => string;
  }) => {
    const groups = useMemo(() => {
      const withDisplay: DestWithDisplay[] = destinations.map((dest) => {
        // The address only means something for EarthRanger; any other
        // technology falls back to the destination's name, as a blank URL does.
        if (!isEarthRangerTypeValue(dest.type.value) || !dest.base_url.trim())
          return { ...dest, displayUrl: dest.name, hasUrl: false };
        let displayUrl = dest.base_url.trim();
        try {
          displayUrl = new URL(dest.base_url).hostname;
        } catch {}
        return { ...dest, displayUrl, hasUrl: true };
      });
      return withDisplay.reduce(
        (acc: Record<string, DestWithDisplay[]>, dest) => {
          if (!acc[dest.type.name]) acc[dest.type.name] = [];
          acc[dest.type.name].push(dest);
          return acc;
        },
        {},
      );
    }, [destinations]);

    const MAX_VISIBLE = 2;
    return (
      <>
        {Object.entries(groups).map(([typeName, dests]) => {
          const visible = dests.slice(0, MAX_VISIBLE);
          const hiddenCount = dests.length - visible.length;
          return (
            <div key={typeName} className="mb-1 last:mb-0">
              <span className="block text-base font-normal text-ui-text-primary-text">
                {typeName}
              </span>
              {visible.map((dest) => (
                <div key={dest.id} className="flex min-w-0 items-center gap-1">
                  <span
                    className="truncate text-xs text-ui-text-secondary-text"
                    title={dest.hasUrl ? dest.base_url : dest.name}
                  >
                    {dest.displayUrl}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(
                        dest.hasUrl ? dest.base_url : dest.name,
                      );
                      toast.info(`${t('copied to clipboard')}`, {
                        position: toast.POSITION.TOP_RIGHT,
                        autoClose: 2000,
                      });
                    }}
                    className="flex-shrink-0 opacity-50 transition-opacity hover:opacity-100"
                    aria-label={t('Copy to clipboard')}
                  >
                    <CopyToClipboard />
                  </button>
                </div>
              ))}
              {hiddenCount > 0 && (
                <HoverTooltip
                  position="right"
                  align="left"
                  width="w-52"
                  content={
                    <div className="flex flex-col gap-1 text-xs">
                      {dests.slice(MAX_VISIBLE).map((d) => (
                        <span
                          key={d.id}
                          className="truncate text-ui-text-secondary-text"
                          title={d.hasUrl ? d.base_url : d.name}
                        >
                          {d.displayUrl}
                        </span>
                      ))}
                    </div>
                  }
                >
                  <span
                    className="cursor-default text-xs font-normal leading-normal text-green-3 underline"
                    style={{
                      textDecorationSkipInk: 'none',
                      textUnderlinePosition: 'from-font',
                    }}
                  >
                    {t('+ {{count}} other', { count: hiddenCount })}
                  </span>
                </HoverTooltip>
              )}
            </div>
          );
        })}
      </>
    );
  },
);
DestinationCellContent.displayName = 'DestinationCellContent';

const HealthLegend = ({ t }: { t: (key: string) => string }) => (
  <div className="text-left text-xs">
    <p className="mb-1 font-semibold text-ui-text-primary-text">
      {t('Route Health')}
    </p>
    <p className="mb-1.5 text-ui-text-primary-text">
      {t('Routes are analyzed every hour.')}
    </p>
    {(['healthy', 'unhealthy', 'disabled'] as const).map((status) => {
      const s = getRouteStatus(status, false, t);
      return (
        <div key={status} className="mb-1 flex items-center gap-1.5">
          <span className="flex-shrink-0">{s.icon}</span>
          <span className="text-ui-text-primary-text">{s.text}</span>
        </div>
      );
    })}
  </div>
);

type RoutesProps = {
  canAddRoute: boolean;
  routes: Route[];
  reloadRoutes: boolean;
  organizations: Organization[];
  destinationUrls: string[];
  providerTypes: ProviderType[];
  loadingProviderTypes: boolean;
  destinationTypes: DestinationType[];
  loadingDestinationTypes: boolean;
  onSelectRoute: Function;
  onChangeGlobalSearch: (value: string) => void;
  onRefresh: () => void;
  onChangeStatusFilter: Function;
  statusFilterValue: any;
  onChangeDestinationUrl: Function;
  destinationFilterValue: any;
  loadingDestinationUrls: boolean;
  onChangeOwner: Function;
  ownerFilterValue: any;
  loadingOrganizations: boolean;
  onChangeProviderType: Function;
  providerTypeFilterValue: any;
  onChangeDestinationType: Function;
  destinationTypeFilterValue: any;
  globalSearchStr: string;
  ordering: string;
  onChangeSortBy: (selected: SortOption) => void;
  onPageClicked: Function;
  totalCount: number;
  pageLoaded: boolean;
  take: number;
  offset: number;
  onClickAdd: MouseEventHandler;
};

const RoutesList = ({
  canAddRoute,
  routes,
  reloadRoutes,
  organizations,
  destinationUrls,
  providerTypes,
  loadingProviderTypes,
  destinationTypes,
  loadingDestinationTypes,
  onSelectRoute,
  onChangeGlobalSearch,
  onRefresh,
  onChangeStatusFilter,
  statusFilterValue,
  onChangeDestinationUrl,
  destinationFilterValue,
  loadingDestinationUrls,
  onChangeOwner,
  ownerFilterValue,
  loadingOrganizations,
  onChangeProviderType,
  providerTypeFilterValue,
  onChangeDestinationType,
  destinationTypeFilterValue,
  globalSearchStr,
  ordering,
  onChangeSortBy,
  onPageClicked,
  totalCount,
  pageLoaded,
  take,
  offset,
  onClickAdd,
}: RoutesProps) => {
  // Phones: a control that does not fully fit in its row is hidden, not cut in half.
  const searchRowRef = useHideOverflowing<HTMLDivElement>();
  const filterRowRef = useHideOverflowing<HTMLDivElement>();
  const { t } = useTranslation();

  const sortOptions: SortOption[] = useMemo(
    () => [
      { label: t('Name (A-Z)'), value: 'name' },
      { label: t('Name (Z-A)'), value: '-name' },
      { label: t('Workspace'), value: 'owner__name' },
      { label: t('Technology'), value: 'type__name' },
    ],
    [t],
  );

  const hasActiveFilters = !!(
    globalSearchStr ||
    statusFilterValue ||
    destinationFilterValue ||
    ownerFilterValue ||
    providerTypeFilterValue ||
    destinationTypeFilterValue
  );

  const clearAllFilters = () => {
    onChangeGlobalSearch('');
    onChangeStatusFilter(null);
    onChangeDestinationUrl(null);
    onChangeOwner(null);
    onChangeProviderType(null);
    onChangeDestinationType(null);
  };

  const routeHealthStatuses = useMemo(() => getRouteHealthStatuses(t), [t]);
  const destinationUrlOptions = useMemo(
    () =>
      destinationUrls.map((url) => {
        let hostname = url;
        try {
          hostname = new URL(url).hostname;
        } catch {}
        return { label: hostname, value: url };
      }),
    [destinationUrls],
  );

  return (
    <div className="transition-all duration-300">
      <div className={listPageClasses.band}>
        <div className={listPageClasses.bandRow}>
          <h1
            data-testid="routes_page_header"
            className={listPageClasses.title}
          >
            {t('Routes')}
          </h1>
          {canAddRoute && (
            <button className={buttonClasses.primary} onClick={onClickAdd}>
              {t('Create Route')}
            </button>
          )}
        </div>
      </div>
      <div className={listPageClasses.body}>
        <div className="mx-auto max-w-list-table">
          {/* Search row */}
          {/* Never wraps: on a phone what does not fit is clipped, not dropped to its own line. */}
          <div
            ref={searchRowRef}
            className="mb-3 flex flex-row flex-nowrap items-end gap-3 overflow-x-clip"
          >
            <GlobalSearch
              onChangeGlobalSearch={onChangeGlobalSearch}
              placeholder={t('Search')}
              savedString={globalSearchStr}
            />
            <SortByDropdown
              options={sortOptions}
              ordering={ordering}
              onChangeSortBy={onChangeSortBy}
            />
            <button
              type="button"
              onClick={() => onRefresh()}
              aria-label={t('Refresh list')}
              disabled={reloadRoutes}
              className={`mb-2 ml-auto flex h-[38px] w-8 shrink-0 cursor-pointer items-center justify-center self-end ${reloadRoutes ? 'origin-center animate-spin' : ''} transition-colors duration-300`}
            >
              <RefreshSpinner />
            </button>
            {hasActiveFilters && (
              <button
                className={`${buttonClasses.clearFilters} mb-2 whitespace-nowrap`}
                onClick={clearAllFilters}
              >
                {t('Clear All Filters')}
              </button>
            )}
          </div>
          {/* Filter dropdowns row */}
          <div
            ref={filterRowRef}
            className="mb-3 flex flex-row flex-nowrap gap-3 overflow-x-clip"
          >
            {/* Provider Type */}
            <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className={filterClasses.label}>{t('Data Provider')}</span>
              <SelectInput
                name="provider_type__in"
                options={providerTypes.map((p) => ({
                  label: p.name,
                  value: p.value,
                }))}
                value={providerTypeFilterValue}
                onChange={onChangeProviderType}
                isMulti={false}
                isClearable={true}
                isSearchable={true}
                isDisabled={false}
                isLoading={loadingProviderTypes}
                className=""
                variant="routeFilter"
              />
            </div>
            {/* Destination Type */}
            <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className={filterClasses.label}>{t('Destination')}</span>
              <SelectInput
                name="destination_type__in"
                options={destinationTypes.map((d) => ({
                  label: d.name,
                  value: d.value,
                }))}
                value={destinationTypeFilterValue}
                onChange={onChangeDestinationType}
                isMulti={false}
                isClearable={true}
                isSearchable={true}
                isDisabled={false}
                isLoading={loadingDestinationTypes}
                className=""
                variant="routeFilter"
              />
            </div>
            {/* Destination */}
            <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className={filterClasses.label}>
                {t('Destination URL')}
              </span>
              <SelectInput
                name="destination_url__in"
                options={destinationUrlOptions}
                value={destinationFilterValue}
                onChange={onChangeDestinationUrl}
                isMulti={false}
                isClearable={true}
                isSearchable={true}
                isDisabled={false}
                isLoading={loadingDestinationUrls}
                className=""
                variant="routeFilter"
              />
            </div>
            {/* Workspace */}
            <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className={filterClasses.label}>{t('Workspace')}</span>
              <SelectInput
                name="owner__in"
                options={organizations.map((org) => ({
                  label: org.name,
                  value: org.id,
                }))}
                value={ownerFilterValue}
                onChange={onChangeOwner}
                isMulti={false}
                isClearable={true}
                isSearchable={true}
                isDisabled={false}
                isLoading={loadingOrganizations}
                className=""
                variant="routeFilter"
              />
            </div>
            {/* Health */}
            <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
              <span className={filterClasses.label}>{t('Health')}</span>
              <SelectInput
                name="status"
                options={routeHealthStatuses.map((s) => ({
                  label: s.name,
                  value: s.value,
                }))}
                value={statusFilterValue}
                onChange={onChangeStatusFilter}
                isMulti={false}
                isClearable={true}
                isSearchable={false}
                isDisabled={false}
                isLoading={false}
                className=""
                variant="routeFilter"
              />
            </div>
          </div>

          {/* Cards */}
          <div className="flex w-full flex-col gap-2">
            {!pageLoaded || reloadRoutes ? (
              [...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-teal-t30 bg-white p-4"
                  data-testid="loading"
                >
                  {/* Title row: name + health icon */}
                  <div className="mb-3 flex items-center gap-2">
                    <div className={`h-5 w-48 ${skeletonClasses.base}`} />
                    <div
                      className={`h-5 w-5 ${skeletonClasses.base} flex-shrink-0 rounded-full`}
                    />
                  </div>
                  {/* Content row */}
                  <div className="flex items-start gap-4">
                    {/* Source */}
                    <div className="flex w-40 flex-shrink-0 flex-col gap-1">
                      <div className={`h-4 w-28 ${skeletonClasses.base}`} />
                      <div className={`h-3 w-24 ${skeletonClasses.base}`} />
                    </div>
                    {/* Arrow */}
                    <div
                      className={`h-5 w-5 ${skeletonClasses.base} mt-0.5 flex-shrink-0`}
                    />
                    {/* Destination */}
                    <div className="flex w-64 flex-col gap-1">
                      <div className={`h-4 w-24 ${skeletonClasses.base}`} />
                      <div className={`h-3 w-32 ${skeletonClasses.base}`} />
                    </div>
                    {/* Workspace */}
                    <div
                      className={`h-4 w-28 ${skeletonClasses.base} flex-shrink-0`}
                    />
                  </div>
                </div>
              ))
            ) : routes.length === 0 ? (
              <div className="rounded-lg border border-teal-t30 bg-white py-16">
                <NoResults
                  headerMsg={t('No Results Found')}
                  labelMsg={t(
                    'You may need to adjust your search or filters to find what you are looking for',
                  )}
                />
              </div>
            ) : (
              routes.map((route: Route) => {
                const routeStatus = getRouteStatus(route.status, false, t);
                // Route pages are addressed by the provider INTEGRATION's id —
                // the id that shows up in backend logs and in EarthRanger's
                // source provider id. `route.id` from /v2/connections/ already
                // IS that integration id; the page resolves the route record
                // from it. A row with no route record still has no Route page,
                // so the guard below stays.
                const routeUrlId =
                  route.id && routeRecordId(route) ? route.id : undefined;
                // An unhealthy Route opens on its Logs tab — the logs are
                // where the reason lives, so start the user there.
                const subpage =
                  route.status === 'unhealthy' ? 'logs' : undefined;
                // No record means no Route page to open — the card stays
                // informative rather than pretending to be a link.
                const navigateToRoute = (e: React.MouseEvent) => {
                  if (!routeUrlId) return;
                  if (e.metaKey || e.ctrlKey) {
                    window.open(
                      `/routes/${routeUrlId}${subpage ? `/${subpage}` : ''}`,
                      '_blank',
                      'noopener,noreferrer',
                    );
                  } else {
                    onSelectRoute(routeUrlId, subpage);
                  }
                };
                return (
                  <div
                    key={route.id}
                    className={`rounded-lg border border-teal-t30 bg-white p-4 ${routeUrlId ? 'cursor-pointer' : 'cursor-default'}`}
                    role={routeUrlId ? 'button' : undefined}
                    tabIndex={routeUrlId ? 0 : undefined}
                    onClick={navigateToRoute}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (routeUrlId && e.key === 'Enter') {
                        e.preventDefault();
                        onSelectRoute(routeUrlId, subpage);
                      }
                    }}
                  >
                    {/* Title row: name + health */}
                    <div className="mb-3 flex items-center gap-2">
                      <span
                        className="truncate text-[18px] font-medium text-green-1"
                        title={routeDisplayName(route)}
                      >
                        {routeDisplayName(route)}
                      </span>
                      <HoverTooltip
                        position="right"
                        align="left"
                        width="w-52"
                        className="flex-shrink-0"
                        ariaLabel={routeStatus.text}
                        content={<HealthLegend t={t} />}
                      >
                        {routeStatus.icon}
                      </HoverTooltip>
                    </div>
                    {/* Content row: source → destination + workspace */}
                    <div className="flex items-start gap-4">
                      {/* Source */}
                      <div className="flex w-40 min-w-0 flex-shrink-0 flex-col">
                        <span className="text-base font-normal text-ui-text-primary-text">
                          {route.provider.type.name}
                        </span>
                        {/* Only EarthRanger's address names something the user recognises; for
                                            other technologies it is an internal endpoint. */}
                        {route.provider.base_url &&
                          isEarthRangerTypeValue(route.provider.type.value) && (
                            <span
                              className="truncate text-xs text-ui-text-secondary-text"
                              title={route.provider.base_url}
                            >
                              {getHostname(route.provider.base_url)}
                            </span>
                          )}
                      </div>
                      {/* Arrow */}
                      <div className="mt-0.5 flex-shrink-0">
                        <ArrowForward
                          size={20}
                          className="text-ui-text-primary-text"
                        />
                      </div>
                      {/* Destination */}
                      <div className="w-[280px] min-w-0 overflow-hidden">
                        {route.destinations.length === 0 ? (
                          <span className="text-sm text-ui-text-primary-text">
                            {t('Destination not set')}
                          </span>
                        ) : (
                          <DestinationCellContent
                            destinations={route.destinations}
                            t={t}
                          />
                        )}
                      </div>
                      {/* Workspace */}
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <span
                          className="block truncate text-sm text-ui-text-primary-text"
                          title={route.owner.name}
                        >
                          {route.owner.name}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            {totalCount > 0 && (
              <Pagination
                showText={true}
                handlePageClick={onPageClicked}
                pageCount={Math.ceil(totalCount / take)}
                resultStart={offset + 1}
                resultEnd={
                  offset + take < totalCount ? offset + take : totalCount
                }
                totalCount={totalCount}
                initialPage={Math.floor(offset / take)}
                take={take}
                loading={!pageLoaded}
                mainclassname={paginationClasses.container}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoutesList;
